<template>
  <div v-if="isVisible" class="establishment-details-modal">
    <div class="details-overlay" @click="closeModal"></div>
    <div class="details-content" :class="getSentimentClass(establishment.rating)">
      <!-- Cabeçalho -->
      <div class="details-header">
        <div class="header-content">
          <div class="rating-badge" :class="getSentimentClass(establishment.rating)">
            {{ establishment.rating.toFixed(1) }}
          </div>
          <div>
            <h2 class="establishment-title">{{ establishment.name }}</h2>
            <p class="establishment-category">{{ getCategoryName(establishment.category) }}</p>
          </div>
        </div>
        <button class="close-details-button" @click="closeModal">
          <font-awesome-icon icon="times" />
        </button>
      </div>
      
      <!-- Conteúdo principal em duas colunas -->
      <div class="details-body">
        <!-- Coluna da esquerda - Informações -->
        <div class="details-info">
          <!-- Informações básicas -->
          <div class="info-section">
            <h3 class="section-title">
              <font-awesome-icon icon="info-circle" />
              Informações
            </h3>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">Endereço</span>
                <span class="info-value address">
                  <font-awesome-icon icon="map-marker-alt" />
                  {{ establishment.formatted_address }}
                </span>
              </div>
              <div class="info-item">
                <span class="info-label">Coordenadas</span>
                <span class="info-value">
                  <font-awesome-icon icon="map-pin" />
                  {{ formatCoordinates(establishment.location) }}
                </span>
              </div>
              <div class="info-item">
                <span class="info-label">Sentimento</span>
                <span 
                  class="info-value sentiment-tag" 
                  :class="getSentimentClass(establishment.rating)"
                >
                  <font-awesome-icon :icon="getSentimentIcon(establishment.rating)" />
                  {{ getSentimentLabel(establishment.rating) }}
                </span>
              </div>
            </div>
          </div>
          
          <!-- Nova seção de Reviews -->
          <div class="info-section reviews-section" v-if="establishment.reviews && establishment.reviews.length > 1">
            <div class="reviews-header">
              <h3 class="section-title">
                <font-awesome-icon icon="star" />
                Avaliações dos Usuários
              </h3>
              <div class="reviews-summary">
                <div class="rating-circle" :class="getSentimentClass(establishment.rating)">
                  {{ establishment.rating.toFixed(1) }}
                </div>
                <div class="reviews-stats">
                  <div class="total-reviews">
                    {{ establishment.review_count || 0 }} avaliações
                  </div>
                  <div class="rating-distribution">
                    <div v-for="n in 5" :key="n" class="rating-bar">
                      <span class="star-number">{{ n }}</span>
                      <div class="bar-container">
                        <div 
                          class="bar-fill" 
                          :style="{ width: `${(getRatingCount(n) / establishment.review_count) * 100}%` }"
                        ></div>
                      </div>
                      <span class="rating-count">{{ getRatingCount(n) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="reviews-list">
              <div 
                v-for="(review, index) in establishment.reviews" 
                :key="index"
                class="review-card"
                :class="getSentimentClass(review.rating)"
                @click="openReviewUrl(review.author_url)"
              >
                <div class="review-header">
                  <div class="reviewer-info">
                    <img 
                      :src="review.photo || '/default-avatar.png'" 
                      :alt="review.name"
                      class="reviewer-avatar"
                    />
                    <div class="reviewer-details">
                      <span class="reviewer-name">{{ review.name }}</span>
                      <div class="review-rating">
                        <font-awesome-icon 
                          v-for="m in 5" 
                          :key="m"
                          icon="star"
                          :class="{ filled: m <= review.rating }"
                        />
                      </div>
                    </div>
                  </div>
                  <span class="review-date">{{ formatReviewDate(review.time_created) }}</span>
                </div>
                <div class="review-content">
                  <p class="review-text">{{ review.text }}</p>
                  <div v-if="review.translated" class="translation-note">
                    <font-awesome-icon icon="language" />
                    Traduzido pelo Google
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Coluna da direita - Mapa e Fotos -->
        <div class="details-visuals">
          <!-- Mapa -->
          <div class="map-container">
            <h3 class="section-title">
              <font-awesome-icon icon="map" />
              Localização
            </h3>
            <div id="details-map" class="details-map"></div>
          </div>
          
          <!-- Fotos -->
          <div class="photos-container">
            <h3 class="section-title">
              <font-awesome-icon icon="images" />
              Fotos
            </h3>
            <div v-if="hasPhotos" class="photos-grid">
              <div 
                v-for="(photo, index) in photos" 
                :key="index"
                class="photo-item"
                @click="openPhotoViewer(index)"
              >
                <img 
                  :src="photo.url" 
                  :alt="'Foto ' + (index + 1) + ' do ' + establishment.name"
                  class="establishment-photo"
                />
              </div>
            </div>
            <div v-else class="no-photos-container">
              <div class="no-photos-icon">
                <font-awesome-icon icon="image" size="4x" />
              </div>
              <p class="no-photos-text">Não há fotos disponíveis para este estabelecimento.</p>
              <p class="no-photos-subtext">Ajude a comunidade compartilhando suas fotos deste lugar!</p>
            </div>
          </div>

          <!-- Avaliações detalhadas -->
          <div class="info-section">
            <h3 class="section-title">
              <font-awesome-icon icon="chart-bar" />
              Avaliações Detalhadas
            </h3>
            <div class="ratings-breakdown">
              <div 
                v-for="(rating, index) in getDetailedRatings()" 
                :key="index"
                class="rating-bar-container"
              >
                <span class="rating-label">{{ rating.label }}</span>
                <div class="rating-bar-wrapper">
                  <div 
                    class="rating-bar" 
                    :style="{ width: `${rating.percentage}%` }"
                    :class="getSentimentClass(rating.value)"
                  ></div>
                </div>
                <span class="rating-value">{{ rating.value.toFixed(1) }}</span>
              </div>
            </div>
          </div>
          
          <!-- Palavras-chave -->
          <div class="info-section">
            <h3 class="section-title">
              <font-awesome-icon icon="tags" />
              Palavras-chave
            </h3>
            <div class="keywords-container">
              <span 
                v-for="(keyword, index) in getKeywords()"
                :key="index"
                class="keyword-pill"
                :class="keyword.type"
              >
                {{ keyword.text }}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Rodapé -->
      <div class="details-footer">
        <div class="footer-info">
          <span class="data-source">
            <font-awesome-icon icon="database" />
            Fonte: Google Places
          </span>
        </div>
        <div class="footer-actions">
          <button class="action-button" @click="closeModal">
            <font-awesome-icon icon="times-circle" />
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import L from 'leaflet';
import axios from 'axios';

export default {
  name: 'EstablishmentDetailsModal',
  props: {
    isVisible: {
      type: Boolean,
      required: true
    },
    establishment: {
      type: Object,
      required: true
    },
    category: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      map: null,
      selectedPhotoIndex: -1,
      isPhotoViewerOpen: false,
      loadingAdditionalData: false,
      establishmentDetails: null
    }
  },
  computed: {
    photos() {
      if (!this.establishment) return [];
      
      // Verifica se tem fotos no formato antigo (photoReferences) ou novo (photos)
      const establishmentPhotos = this.establishment.photos || [];
      
      // Se não houver fotos, retorna array vazio
      if (!establishmentPhotos.length) {
        return [];
      }
      
      // Retorna as fotos com todos os detalhes para o grid
      return establishmentPhotos;
    },
    photoUrls() {
      // Retorna apenas as URLs das fotos para o lightbox
      return this.photos.map(photo => photo.url);
    },
    hasPhotos() {
      return this.photos && this.photos.length > 0;
    }
  },
  mounted() {
    if (this.isVisible) {
      this.$nextTick(() => {
        this.initMap();
        this.loadBusinessDetails();
      });
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.initMap();
          this.loadBusinessDetails();
        });
      }
    }
  },
  methods: {
    loadBusinessDetails() {
      if (!this.establishment || !this.establishment.id) return;
      
      this.loadingAdditionalData = true;
      
      axios.get(`/api/reviews/business/${this.establishment.id}`)
        .then(response => {
          this.establishmentDetails = response.data.business;
          this.establishmentDetails.photos = response.data.photos;
        })
        .catch(error => {
          console.error('Erro ao carregar detalhes do estabelecimento:', error);
        })
        .finally(() => {
          this.loadingAdditionalData = false;
        });
    },
    closeModal() {
      this.$emit('close');
    },
    getSentimentClass(rating) {
      if (rating >= 4) return 'positive';
      if (rating >= 2) return 'neutral';
      return 'negative';
    },
    getSentimentLabel(rating) {
      if (rating >= 4) return 'Positivo';
      if (rating >= 2) return 'Neutro';
      return 'Negativo';
    },
    getSentimentIcon(rating) {
      if (rating >= 4) return 'smile';
      if (rating >= 2) return 'meh';
      return 'frown';
    },
    getCategoryName(categoryCode) {
      const categories = {
        'gastronomy': 'Gastronomia',
        'lodging': 'Hospedagem',
        'tour': 'Atração Turística',
        'transport': 'Transporte',
        'nightlife': 'Vida Noturna',
        'commerce': 'Comércio'
      };
      return categories[categoryCode] || categoryCode;
    },
    formatCoordinates(location) {
      if (!location) return 'N/A';
      return `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`;
    },
    formatDate(date) {
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    },
    getDetailedRatings() {
      // Simulação de avaliações detalhadas
      const baseRating = this.establishment.rating || 3;
      
      // Criamos algumas variações baseadas na avaliação principal
      return [
        { label: 'Atendimento', value: Math.min(5, baseRating + Math.random() * 0.5), percentage: Math.min(5, baseRating + Math.random() * 0.5) * 20 },
        { label: 'Limpeza', value: Math.min(5, baseRating + Math.random() * 0.6), percentage: Math.min(5, baseRating + Math.random() * 0.6) * 20 },
        { label: 'Localização', value: Math.min(5, baseRating + Math.random() * 0.8), percentage: Math.min(5, baseRating + Math.random() * 0.8) * 20 },
        { label: 'Custo-Benefício', value: Math.min(5, baseRating - Math.random() * 0.5), percentage: Math.min(5, baseRating - Math.random() * 0.5) * 20 },
        { label: 'Qualidade', value: Math.min(5, baseRating - Math.random() * 0.3), percentage: Math.min(5, baseRating - Math.random() * 0.3) * 20 }
      ];
    },
    getKeywords() {
      // Simulação de palavras-chave baseadas no sentimento
      const rating = this.establishment.rating || 3;
      const keywords = [];
      
      // Palavras positivas
      if (rating >= 3) {
        const positiveWords = ['Excelente', 'Ótimo', 'Recomendo', 'Qualidade', 'Atencioso', 'Maravilhoso'];
        for (let i = 0; i < Math.min(rating, 3); i++) {
          keywords.push({
            text: positiveWords[Math.floor(Math.random() * positiveWords.length)],
            type: 'positive'
          });
        }
      }
      
      // Palavras neutras
      const neutralWords = ['Padrão', 'Regular', 'Comum', 'Básico', 'Aceitável'];
      for (let i = 0; i < Math.floor(Math.random() * 3); i++) {
        keywords.push({
          text: neutralWords[Math.floor(Math.random() * neutralWords.length)],
          type: 'neutral'
        });
      }
      
      // Palavras negativas
      if (rating < 4) {
        const negativeWords = ['Demorado', 'Caro', 'Simples', 'Melhorar', 'Insatisfeito'];
        for (let i = 0; i < Math.max(1, 5 - rating); i++) {
          keywords.push({
            text: negativeWords[Math.floor(Math.random() * negativeWords.length)],
            type: 'negative'
          });
        }
      }
      
      return keywords;
    },
    initMap() {
      if (!this.establishment.location) return;
      
      // Se já tiver um mapa, destrua
      if (this.map) {
        this.map.remove();
      }
      
      // Inicialize o mapa
      setTimeout(() => {
        const mapElement = document.getElementById('details-map');
        if (!mapElement) return;
        
        this.map = L.map('details-map').setView([
          this.establishment.location.lat,
          this.establishment.location.lng
        ], 15);
        
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }).addTo(this.map);
        
        // Adicione um marcador
        const marker = L.marker([
          this.establishment.location.lat,
          this.establishment.location.lng
        ]).addTo(this.map);
        
        // Adicione um popup com o nome do estabelecimento
        marker.bindPopup(`<b>${this.establishment.name}</b><br>${this.establishment.formatted_address}`).openPopup();
      }, 300);
    },
    openPhotoViewer(index) {
      this.selectedPhotoIndex = index;
      this.isPhotoViewerOpen = true;
    },
    closePhotoViewer() {
      this.isPhotoViewerOpen = false;
    },
    getRatingCount(rating) {
      // Simulação da contagem de avaliações por estrela
      const total = this.establishment.review_count || 0;
      const baseRating = this.establishment.rating || 3;
      const distribution = [0.1, 0.15, 0.2, 0.3, 0.25]; // Distribuição aproximada
      return Math.round(total * distribution[rating - 1]);
    },
    formatReviewDate(timestamp) {
      if (!timestamp) return 'Data não disponível';
      
      try {
        // Se for uma string no formato YYYY-MM-DD HH:mm:ss
        if (typeof timestamp === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(timestamp)) {
          const date = new Date(timestamp);
          return new Intl.DateTimeFormat('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }).format(date);
        }
        
        return 'Data não disponível';
      } catch (error) {
        console.error('Erro ao formatar data:', error);
        return 'Data não disponível';
      }
    },
    openReviewUrl(url) {
      if (url) {
        window.open(url, '_blank');
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.establishment-details-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
}

.details-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(5px);
}

.details-content {
  position: relative;
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  
  &.positive::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: #0b8c0b;
  }
  
  &.neutral::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: #ffc700;
  }
  
  &.negative::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: #f25362;
  }
}

/* Cabeçalho */
.details-header {
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eee;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.rating-badge {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  font-weight: bold;
  color: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  
  &.positive { background: #0b8c0b; }
  &.neutral { background: #ffc700; }
  &.negative { background: #f25362; }
}

.establishment-title {
  margin: 0;
  font-size: 1.8rem;
  color: #2c3e50;
}

.establishment-category {
  margin: 0.25rem 0 0;
  font-size: 1rem;
  color: #7f8c8d;
}

.close-details-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #95a5a6;
  cursor: pointer;
  transition: color 0.3s;
  
  &:hover {
    color: #2c3e50;
  }
}

/* Corpo do Modal */
.details-body {
  flex: 1;
  display: flex;
  overflow: auto;
  padding: 1.5rem 2rem;
  
  @media (max-width: 1024px) {
    flex-direction: column;
  }
}

/* Informações */
.details-info {
  flex: 1;
  padding-right: 1.5rem;
  
  @media (max-width: 1024px) {
    padding-right: 0;
    padding-bottom: 1.5rem;
  }
}

.info-section {
  margin-bottom: 2rem;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.section-title {
  margin-top: 0;
  margin-bottom: 1.25rem;
  font-size: 1.2rem;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: #3498db;
  }
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-label {
  font-size: 0.9rem;
  color: #7f8c8d;
}

.info-value {
  font-size: 1rem;
  color: #2c3e50;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &.address {
    font-size: 0.95rem;
  }
  
  svg {
    color: #3498db;
  }
  
  &.sentiment-tag {
    display: inline-flex;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.9rem;
    
    &.positive { 
      background: rgba(11, 140, 11, 0.1);
      color: #0b8c0b;
      
      svg {
        color: #0b8c0b;
      }
    }
    
    &.neutral { 
      background: rgba(255, 199, 0, 0.1);
      color: #b78e00;
      
      svg {
        color: #b78e00;
      }
    }
    
    &.negative { 
      background: rgba(242, 83, 98, 0.1);
      color: #f25362;
      
      svg {
        color: #f25362;
      }
    }
  }
}

/* Avaliações detalhadas */
.ratings-breakdown {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.rating-bar-container {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.rating-label {
  flex: 0 0 120px;
  font-size: 0.9rem;
  color: #7f8c8d;
}

.rating-bar-wrapper {
  flex: 1;
  height: 8px;
  background: #ecf0f1;
  border-radius: 4px;
  overflow: hidden;
}

.rating-bar {
  height: 100%;
  border-radius: 4px;
  
  &.positive { background: #0b8c0b; }
  &.neutral { background: #ffc700; }
  &.negative { background: #f25362; }
}

.rating-value {
  flex: 0 0 30px;
  text-align: right;
  font-weight: 600;
  color: #2c3e50;
}

/* Palavras-chave */
.keywords-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.keyword-pill {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  
  &.positive {
    background: rgba(11, 140, 11, 0.1);
    color: #0b8c0b;
  }
  
  &.neutral {
    background: rgba(255, 199, 0, 0.1);
    color: #b78e00;
  }
  
  &.negative {
    background: rgba(242, 83, 98, 0.1);
    color: #f25362;
  }
}

/* Visuais */
.details-visuals {
  flex: 1;
  
  @media (max-width: 1024px) {
    width: 100%;
  }
}

.map-container {
  margin-bottom: 2rem;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.details-map {
  height: 300px;
  border-radius: 8px;
  overflow: hidden;
}

.photos-container {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.photos-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.photo-item {
  aspect-ratio: 4/3;
  background: #ecf0f1;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.establishment-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.no-photos-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
}

.no-photos-icon {
  font-size: 4rem;
  color: #7f8c8d;
  margin-bottom: 1rem;
}

.no-photos-text {
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.no-photos-subtext {
  font-size: 0.9rem;
  color: #7f8c8d;
}

/* Rodapé */
.details-footer {
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #eee;
  background: #f8f9fa;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
}

.footer-info {
  display: flex;
  gap: 1.5rem;
  color: #7f8c8d;
  font-size: 0.9rem;
  
  svg {
    margin-right: 0.5rem;
  }
}

.footer-actions {
  display: flex;
  gap: 1rem;
}

.action-button {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  background: #ecf0f1;
  color: #7f8c8d;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: #e0e6eb;
    color: #2c3e50;
  }
  
  &.share-button {
    background: #3498db;
    color: white;
    
    &:hover {
      background: #2980b9;
    }
  }
}

/* Estilos para a nova seção de Reviews */
.reviews-section {
  .reviews-header {
    margin-bottom: 2rem;
  }

  .reviews-summary {
    display: flex;
    gap: 2rem;
    align-items: flex-start;
    margin-top: 1rem;
  }

  .rating-circle {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    font-weight: bold;
    color: white;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    
    &.positive { background: #0b8c0b; }
    &.neutral { background: #ffc700; }
    &.negative { background: #f25362; }
  }

  .reviews-stats {
    flex: 1;

    .total-reviews {
      font-size: 1.2rem;
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 1rem;
    }

    .rating-distribution {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .rating-bar {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      .star-number {
        width: 20px;
        color: #7f8c8d;
      }

      .bar-container {
        flex: 1;
        height: 8px;
        background: #ecf0f1;
        border-radius: 4px;
        overflow: hidden;
      }

      .bar-fill {
        height: 100%;
        background: #ffc700;
        border-radius: 4px;
        transition: width 0.3s ease;
      }

      .rating-count {
        width: 30px;
        text-align: right;
        color: #7f8c8d;
        font-size: 0.9rem;
      }
    }
  }

  .reviews-list {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .review-card {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    border-left: 4px solid transparent;
    cursor: pointer;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }

    &.positive { border-left-color: #0b8c0b; }
    &.neutral { border-left-color: #ffc700; }
    &.negative { border-left-color: #f25362; }

    .review-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 1rem;
    }

    .reviewer-info {
      display: flex;
      align-items: center;
      gap: 1rem;
    }

    .reviewer-avatar {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      object-fit: cover;
    }

    .reviewer-details {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;

      .reviewer-name {
        font-weight: 600;
        color: #2c3e50;
      }

      .review-rating {
        display: flex;
        gap: 0.25rem;

        svg {
          font-size: 0.875rem;
          color: #e2e8f0;

          &.filled {
            color: #ffc700;
          }
        }
      }
    }

    .review-date {
      font-size: 0.875rem;
      color: #7f8c8d;
    }

    .review-content {
      .review-text {
        color: #2c3e50;
        line-height: 1.6;
        margin-bottom: 0.5rem;
      }

      .translation-note {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.875rem;
        color: #7f8c8d;
        margin-top: 0.5rem;

        svg {
          color: #3498db;
        }
      }
    }
  }
}
</style>

<style>
/* Ajustes para o Leaflet */
.details-map .leaflet-control-container {
  visibility: visible !important;
  opacity: 1 !important;
  pointer-events: auto !important;
}
</style> 